"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Heading, Modal, Text, TextField, Link } from "ui-components";
import { z } from "zod";

import { ORGANIZATION_INQUIRY_INTEREST_LABELS } from "@/src/constants/organizationInquiryInterests";
import {
  CreateOrganizationInquiryDtoNumberOfEmployees,
  OrganizationInquiryInterestsDto,
  UserUpdateProfileDtoCountry,
  useOrganizationAddLead,
} from "@/src/hooks/api/generated";
import { createSalesforceLeadCorporateMember } from "@/utils/salesForce";

import { makeToast } from "../Toast/Toast";

import {
  BecomeACorporateMemberSchemaType,
  becomeACorporateMemberSchema,
} from "./schema/become-a-corporate-member";

const freeEmailDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "live.com",
];

const ORGANIZATION_SIZE_LABELS: Record<
  CreateOrganizationInquiryDtoNumberOfEmployees,
  string
> = {
  "0-500": "0–500",
  "501-1000": "501–1,000",
  "1001-5000": "1,001–5,000",
  "5001 and above": "5,001+",
};

const ORGANIZATION_SIZE_OPTIONS = Object.values(
  CreateOrganizationInquiryDtoNumberOfEmployees
).map((id) => ({
  id,
  label: ORGANIZATION_SIZE_LABELS[id],
  sub: "employees",
}));

type InterestKey = keyof OrganizationInquiryInterestsDto;

const INTEREST_OPTIONS: {
  id: InterestKey;
  label: string;
  icon: JSX.Element;
}[] = [
  {
    id: "hire_diverse_talent",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.hire_diverse_talent,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: "advertise_events",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.advertise_events,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
      </svg>
    ),
  },
  {
    id: "reach_graduates",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.reach_graduates,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c3 3 9 3 12 0v-5" />
      </svg>
    ),
  },
  {
    id: "reach_professionals",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.reach_professionals,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    id: "employer_branding",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.employer_branding,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="8" r="6" />
        <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
      </svg>
    ),
  },
  {
    id: "erg_solutions",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.erg_solutions,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="10" cy="8" r="5" />
        <path d="M18 21a8 8 0 0 0-16 0" />
        <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
      </svg>
    ),
  },
  {
    id: "dei_learning_products",
    label: ORGANIZATION_INQUIRY_INTEREST_LABELS.dei_learning_products,
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 7v14" />
        <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
      </svg>
    ),
  },
];

const corporateSolutionsSchema = z.intersection(
  becomeACorporateMemberSchema,
  z.object({
    full_name: z.string().min(1, { message: "Please enter your full name" }),
    interests: z
      .array(z.string())
      .min(1, { message: "Please choose at least one" }),
    email: z
      .string()
      .min(1, { message: "Please Enter Your Email" })
      .email({ message: "Invalid email address." })
      .refine(
        (email) => {
          const [, domain] = email.split("@");
          return !freeEmailDomains.includes(domain?.toLowerCase() || "");
        },
        {
          message: "Please enter a valid work email address.",
        }
      ),
  })
);

type CorporateSolutionsSchemaType = z.infer<typeof corporateSolutionsSchema>;

interface ICorporateSolutionsLeadFormProps {
  intent?: "primary" | "secondary" | "success" | "danger";
  triggerButtonSize?: "small" | "medium";
  triggerButtonText?: string;
  triggerButtonClasses?: string;
  isModal?: boolean;
  submitButtonText?: string;
  title?: string;
  text?: string;
  acknowledgmentText?: string;
  acknowledgmentTitle?: string;
  acknowledgmentLink?: string;
}

export default function CorporateSolutionsLeadForm({
  intent = "primary",
  triggerButtonSize = "medium",
  triggerButtonText = "Enquire now",
  triggerButtonClasses = "",
  isModal = true,
  submitButtonText,
  title,
  text,
  acknowledgmentText,
  acknowledgmentTitle,
  acknowledgmentLink,
}: ICorporateSolutionsLeadFormProps): JSX.Element {
  const [isShowModal, setIsShowModal] = useState(false);
  const [step, setStep] = useState<number>(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [nextStepChoice, setNextStepChoice] = useState<string>("");
  const [connectType, setConnectType] = useState<string>("");

  const { mutate: createLead, status } = useOrganizationAddLead();

  const {
    register,
    getFieldState,
    setValue,
    watch,
    trigger,
    formState: { errors },
    reset,
  } = useForm<CorporateSolutionsSchemaType>({
    resolver: zodResolver(corporateSolutionsSchema),
    mode: "onChange",
    defaultValues: {
      interests: [],
      first_name: "N/A", // Hidden default
      last_name: "N/A", // Hidden default
      contact_number: "", // Hidden default
      is_erg_member: "No", // Hidden default
      advertise_events: "Yes", // Default fallback, overriden
      diverse_talent: "Yes", // Default fallback, overriden
      reach_to: "All", // Default fallback, overriden
      privacyPolicy: true, // Auto-consented based on design
    } as unknown as CorporateSolutionsSchemaType,
  });

  const formData = watch();

  const handleNext = async (): Promise<void> => {
    let fieldsToValidate: (keyof CorporateSolutionsSchemaType)[] = [];
    if (step === 1) {
      fieldsToValidate = ["full_name", "email", "organization_name"];
    } else if (step === 2) {
      fieldsToValidate = ["number_of_employees", "interests"];
    }

    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = (): void => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  // Top step indicators allow navigating back to an earlier (already-visited)
  // step. Forward progress stays gated behind the validated Continue button.
  const handleStepClick = (target: number): void => {
    if (target < step) {
      setStep(target);
    }
  };

  const onSubmit = (data: CorporateSolutionsSchemaType): void => {
    // Attempting to parse first/last name
    const nameParts = data.full_name?.trim().split(" ") || [];
    const firstName = nameParts[0] || "N/A";
    const lastName =
      nameParts.length > 1 ? nameParts.slice(1).join(" ") : "N/A";

    const interests = INTEREST_OPTIONS.reduce<OrganizationInquiryInterestsDto>(
      (acc, option) => ({
        ...acc,
        [option.id]: data.interests?.includes(option.id) ?? false,
      }),
      {}
    );

    createLead(
      {
        data: {
          first_name: firstName || "N/A",
          last_name: lastName,
          email: data.email,
          organization_name: data.organization_name,
          number_of_employees: data.number_of_employees,
          contact_number: data.contact_number,
          country: UserUpdateProfileDtoCountry.GB,
          terms_consent: data.privacyPolicy,
          privacy_consent: data.privacyPolicy,
          initiatives: "No",
          is_erg_member: "No",
          interests,
        },
      },
      {
        onSuccess: (res) => {
          if (nextStepChoice === "book") {
            window.open(
              "https://bookings.cloud.microsoft/bookwithme/user/2c0fea1789644054bf3b2bea616e5d45%40mygwork.com?anonymous&ismsaljsauthenabled ",
              "_blank"
            );
          }
          setStep(4);
          setIsSubmitted(true);

          // Map to BecomeACorporateMemberSchemaType for existing salesforce method
          const salesforcePayload: BecomeACorporateMemberSchemaType = {
            ...data,
            first_name: firstName || "N/A",
            last_name: lastName,
            email: data.email,
            organization_name: data.organization_name,
            number_of_employees: data.number_of_employees,
            contact_number: data.contact_number,
            is_erg_member: "No",
          };

          createSalesforceLeadCorporateMember(
            salesforcePayload,
            res,
            interests
          );
          setConnectType(nextStepChoice);
          setNextStepChoice("");
        },
        onError: () =>
          makeToast({ intent: "danger", title: "Something went wrong." }),
      }
    );
  };

  const handleFinalSubmit = async (): Promise<void> => {
    const isPhoneValid = await trigger("contact_number");
    if (!isPhoneValid) {
      return;
    }

    onSubmit(watch());
  };

  const renderStep1 = (): JSX.Element => (
    <div className="flex h-full flex-col w-full mt-4">
      <div className="flex flex-col gap-3">
        <TextField
          label="Full name"
          placeholder="Full name"
          isRequired
          register={register("full_name")}
          errorMessage={errors.full_name?.message}
          hasValue={getFieldState("full_name").isDirty}
        />
        <TextField
          label="Work email"
          placeholder="Work email"
          isRequired
          register={register("email")}
          errorMessage={errors.email?.message}
          hasValue={getFieldState("email").isDirty}
        />
        <TextField
          label="Company name"
          placeholder="Company name"
          isRequired
          register={register("organization_name")}
          errorMessage={errors.organization_name?.message}
          hasValue={getFieldState("organization_name").isDirty}
        />
        <div className="mt-1 w-full">
          <Text className="text-gray-600 !text-md">
            By submitting, you agree to myGwork&apos;s{" "}
            <Link
              className="underline"
              href="/terms-of-use"
              target="_blank"
              onClick={(event) => event.stopPropagation()}
            >
              Terms
            </Link>{" "}
            &{" "}
            <Link
              className="underline"
              href="/privacy-policy"
              target="_blank"
              onClick={(event) => event.stopPropagation()}
            >
              Privacy Policy
            </Link>
            .
          </Text>
        </div>
      </div>
      <div className="flex justify-end mt-auto pt-4">
        <Button
          onPress={handleNext}
          className="w-full md:w-auto px-8"
          intent="primary"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderStep2 = (): JSX.Element => (
    <div className="flex h-full flex-col w-full gap-5 mt-3">
      <div>
        <div className="flex justify-between items-center mb-2">
          <Text variant="small" className="font-semibold text-black">
            Organisation size
          </Text>
          <Text
            variant="small"
            className={
              errors.number_of_employees ? "text-red-600" : "text-gray-500"
            }
          >
            {errors.number_of_employees?.message
              ? errors.number_of_employees.message
              : "Select one"}
          </Text>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {ORGANIZATION_SIZE_OPTIONS.map((sizeObj) => {
            const isSelected = formData.number_of_employees === sizeObj.id;
            return (
              <div
                key={sizeObj.id}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setValue("number_of_employees", sizeObj.id);
                    trigger("number_of_employees");
                  }
                }}
                onClick={() => {
                  setValue("number_of_employees", sizeObj.id);
                  trigger("number_of_employees");
                }}
                className={`cursor-pointer px-3 py-2 border rounded-xl text-center transition-shadow ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-gray-300 hover:border-primary hover:shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
                }`}
              >
                <div className="text-sm font-bold leading-tight text-black">
                  {sizeObj.label}{" "}
                  <span
                    className={`font-normal ${
                      isSelected ? "text-gray-600" : "text-gray-500"
                    }`}
                  >
                    {sizeObj.sub}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <Text variant="small" className="font-semibold text-black">
            What are you interested in?
          </Text>
          <Text
            variant="small"
            className={errors.interests ? "text-red-600" : "text-gray-500"}
          >
            {errors.interests
              ? errors.interests.message
              : "Select all that apply"}
          </Text>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {INTEREST_OPTIONS.map((interest, index) => {
            const isSelected = formData.interests?.includes(interest.id);
            const spansFullRow =
              INTEREST_OPTIONS.length % 2 === 1 &&
              index === INTEREST_OPTIONS.length - 1;
            return (
              <div
                key={interest.id}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    const current = formData.interests || [];
                    const updated = isSelected
                      ? current.filter((i: string) => i !== interest.id)
                      : [...current, interest.id];
                    setValue("interests", updated);
                    trigger("interests");
                  }
                }}
                onClick={() => {
                  const current = formData.interests || [];
                  const updated = isSelected
                    ? current.filter((i: string) => i !== interest.id)
                    : [...current, interest.id];
                  setValue("interests", updated);
                  trigger("interests");
                }}
                className={`cursor-pointer px-3 py-2 border rounded-xl flex items-center gap-3 transition-shadow ${
                  spansFullRow ? "sm:col-span-2" : ""
                } ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-gray-300 hover:border-primary hover:shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
                }`}
              >
                <div
                  className={`flex items-center justify-center p-1.5 rounded-lg ${
                    isSelected
                      ? "bg-primary/10 text-primary"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {interest.icon}
                </div>
                <span className="text-sm font-semibold text-black">
                  {interest.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between items-center mt-auto pt-2">
        <button
          onClick={handleBack}
          className="text-black font-semibold hover:underline"
        >
          Back
        </button>
        <Button
          isDisabled={status === "pending"}
          isLoading={status === "pending"}
          onPress={handleNext}
          className="w-auto px-8"
          intent="primary"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderStep3 = (): JSX.Element => (
    <div className="flex h-full flex-col w-full gap-8 mt-4">
      <div className="flex flex-col gap-4">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setNextStepChoice("book");
            }
          }}
          onClick={() => setNextStepChoice("book")}
          className={`cursor-pointer px-4 py-5 border rounded-xl flex items-center gap-4 transition-shadow ${
            nextStepChoice === "book"
              ? "border-primary bg-primary/5"
              : "border-gray-300 hover:border-primary hover:shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
          }`}
        >
          <div
            className={`flex items-center justify-center p-3 rounded-xl ${
              nextStepChoice === "book"
                ? "bg-primary/10 text-primary"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-md font-semibold text-black">
              Book a call
            </span>
            <span className="text-sm text-gray-500">
              Pick a time that suits you
            </span>
          </div>
        </div>

        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setNextStepChoice("callback");
            }
          }}
          onClick={() => setNextStepChoice("callback")}
          className={`cursor-pointer px-4 py-5 border rounded-xl flex items-center gap-4 transition-shadow ${
            nextStepChoice === "callback"
              ? "border-primary bg-primary/5"
              : "border-gray-300 hover:border-primary hover:shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
          }`}
        >
          <div
            className={`flex items-center justify-center p-3 rounded-xl ${
              nextStepChoice === "callback"
                ? "bg-primary/10 text-primary"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-md font-semibold text-black">
              Request a callback
            </span>
            <span className="text-sm text-gray-500">
              We&apos;ll call you shortly
            </span>
          </div>
        </div>

        <div className="mt-2">
          <TextField
            label="Phone number"
            placeholder="Your phone number"
            isRequired
            register={register("contact_number")}
            errorMessage={errors.contact_number?.message}
            hasValue={getFieldState("contact_number").isDirty}
          />
          <Text variant="small" className="text-gray-500">
            We&lsquo;ll only use this to get in touch about your enquiry.
          </Text>
        </div>
      </div>

      <div className="flex justify-between items-center mt-auto pt-4">
        <button
          onClick={handleBack}
          className="text-black font-semibold hover:underline"
        >
          Back
        </button>
        <Button
          intent="primary"
          isDisabled={!nextStepChoice || status === "pending"}
          isLoading={status === "pending"}
          className="w-auto px-8"
          onPress={handleFinalSubmit}
        >
          {submitButtonText || "Done"}
        </Button>
      </div>
    </div>
  );

  const renderStep4 = (): JSX.Element => (
    <div className="flex flex-col items-center justify-center w-full gap-2 py-8 text-center animate-in fade-in zoom-in-95">
      <div className="w-16 h-16 rounded-full bg-brand-primary-light flex items-center justify-center mb-2">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#F2783B"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <Heading className="!text-xl font-bold text-black mb-1">
        {acknowledgmentTitle || "Thanks for getting in touch"}
      </Heading>

      {acknowledgmentText ? (
        <Text className="text-gray-600 !text-lg max-w-md">
          {acknowledgmentText}
        </Text>
      ) : connectType === "book" ? (
        <Text className="text-gray-600 !text-lg max-w-md text-center">
          We&apos;ve opened the booking page in a new tab — finish picking a
          time whenever you&apos;re ready.
        </Text>
      ) : (
        <Text className="text-gray-600 !text-md max-w-md">
          We&apos;ll call you on {formData.contact_number} shortly.
        </Text>
      )}

      {acknowledgmentLink ? (
        <Link
          href={acknowledgmentLink}
          target="_blank"
          className="mt-4 text-primary font-semibold underline"
        >
          Explore myGwork
        </Link>
      ) : (
        <Text className="text-gray-600 !text-md mt-2">
          We look forward to connecting with you.
        </Text>
      )}
    </div>
  );

  const renderContent = (): JSX.Element => (
    <div
      className={`flex flex-col w-full px-4 md:px-10 gap-2 ${
        isModal ? "max-h-[85vh] overflow-y-auto pb-2" : ""
      }`}
    >
      {step !== 4 && (
        <div
          className={`flex items-center justify-between mt-2 mb-4 relative ${
            step === 4 ? "opacity-0 pointer-events-none absolute" : ""
          }`}
        >
          <div className="absolute top-[12px] left-[15%] right-[15%] h-[2px] bg-gray-200 z-0">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: step === 1 ? "0%" : step === 2 ? "50%" : "100%" }}
            />
          </div>

          <div
            role="button"
            tabIndex={step > 1 ? 0 : -1}
            onClick={() => handleStepClick(1)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && step > 1) {
                e.preventDefault();
                handleStepClick(1);
              }
            }}
            className={`flex flex-col items-center gap-2 bg-white px-2 relative z-10 ${
              step > 1 ? "cursor-pointer" : ""
            }`}
          >
            {step > 1 ? (
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            ) : (
              <div className="w-6 h-6 rounded-full border-2 border-primary/20 bg-primary/10 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-primary" />
              </div>
            )}
            <span
              className={`text-xs ${
                step >= 1 ? "text-black font-semibold" : "text-gray-500"
              }`}
            >
              Basic details
            </span>
          </div>

          <div
            role="button"
            tabIndex={step > 2 ? 0 : -1}
            onClick={() => handleStepClick(2)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && step > 2) {
                e.preventDefault();
                handleStepClick(2);
              }
            }}
            className={`flex flex-col items-center gap-2 bg-white px-2 relative z-10 ${
              step > 2 ? "cursor-pointer" : ""
            }`}
          >
            {step > 2 ? (
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            ) : step === 2 ? (
              <div className="w-6 h-6 rounded-full border-2 border-primary/20 bg-primary/10 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-primary" />
              </div>
            ) : (
              <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-white" />
            )}
            <span
              className={`text-xs ${
                step >= 2 ? "text-black font-semibold" : "text-gray-400"
              }`}
            >
              Organisation
            </span>
          </div>

          <div className="flex flex-col items-center gap-2 bg-white px-2 relative z-10">
            {step === 3 ? (
              <div className="w-6 h-6 rounded-full border-2 border-primary/20 bg-primary/10 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-primary" />
              </div>
            ) : (
              <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-white" />
            )}
            <span
              className={`text-xs ${
                step >= 3 ? "text-black font-semibold" : "text-gray-400"
              }`}
            >
              Next steps
            </span>
          </div>
        </div>
      )}

      {step === 4 ? (
        renderStep4()
      ) : (
        // Steps 1-3 share a single grid cell, so the card is always as tall as
        // the tallest of them and does not resize as the user moves between
        // steps. Inactive steps are `invisible`, which also takes them out of
        // the tab order.
        <div className="grid">
          <div
            className={`col-start-1 row-start-1 flex flex-col gap-2 w-full ${
              step === 1 ? "" : "invisible pointer-events-none"
            }`}
            aria-hidden={step !== 1}
          >
            <Heading className="!text-xl font-bold text-black">
              {title || "Let's get to know you"}
            </Heading>
            <Text className="text-gray-600 !text-md">
              {text ||
                "A few quick details so we can point you to the right team."}
            </Text>
            {renderStep1()}
          </div>

          <div
            className={`col-start-1 row-start-1 flex flex-col gap-2 w-full ${
              step === 2 ? "" : "invisible pointer-events-none"
            }`}
            aria-hidden={step !== 2}
          >
            <Heading className="!text-xl font-bold text-black mb-1">
              Tell us about your organisation
            </Heading>
            <Text className="text-gray-600 !text-md">
              This helps us tailor the right package for you.
            </Text>
            {renderStep2()}
          </div>

          <div
            className={`col-start-1 row-start-1 flex flex-col gap-2 w-full ${
              step === 3 ? "" : "invisible pointer-events-none"
            }`}
            aria-hidden={step !== 3}
          >
            <Heading className="!text-xl font-bold text-black mb-1">
              How would you like to connect?
            </Heading>
            <Text className="text-gray-600 !text-md">
              Choose what works best — we&apos;ll take it from here.
            </Text>
            {renderStep3()}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {isModal && (
        <Button
          onPress={() => setIsShowModal(true)}
          intent={intent}
          size={triggerButtonSize === "small" ? "xs" : "md"}
          className={triggerButtonClasses}
        >
          {triggerButtonText}
        </Button>
      )}

      {isModal ? (
        <Modal
          isOpen={isShowModal}
          onOpenChange={(open) => {
            setIsShowModal(open);
            if (!open && isSubmitted) {
              // Reset if closed after submission
              reset();
              setStep(1);
              setIsSubmitted(false);
            }
          }}
          isBackgroundBlur
          animatedModalClassName="md:min-w-[600px] flex flex-1 items-center justify-center"
        >
          {renderContent()}
        </Modal>
      ) : (
        <div className="w-full">{renderContent()}</div>
      )}
    </div>
  );
}
